import type {
  GuestCheckoutAddress,
  GuestCheckoutCache,
  GuestCheckoutCacheOptions,
  GuestCheckoutCachePatch,
  GuestCheckoutStorage,
} from './types';

export const GUEST_CHECKOUT_CACHE_BASE_KEY = 'zl-guest-checkout-cache' as const;

const VISITOR_ID_STORAGE_KEY = 'zl-visitor_id';
const memoryCaches = new Map<string, string>();
const memoryFallbackKeys = new Set<string>();
const clearedCacheKeys = new Set<string>();

const addressFields: Array<keyof GuestCheckoutAddress> = [
  'id',
  'is_pobox',
  'country_id',
  'country',
  'province_id',
  'province',
  'city',
  'street',
  'apartment',
  'postcode',
  'cpf',
  'tel_code',
  'tel_country',
  'telephone',
  'firstname',
  'lastname',
  'address',
  'default',
];

function getBrowserLocalStorage(): GuestCheckoutStorage | null {
  try {
    if (typeof globalThis.localStorage === 'undefined') return null;
    return globalThis.localStorage;
  } catch {
    return null;
  }
}

function resolveStorage(storage?: GuestCheckoutStorage | null): GuestCheckoutStorage | null {
  return storage === undefined ? getBrowserLocalStorage() : storage;
}

function normalizeText(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim();
  return normalized || undefined;
}

function normalizeId(value: unknown): string | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return normalizeText(value);
}

function normalizeAddress(
  value: unknown,
  { createId = false }: { createId?: boolean } = {},
): GuestCheckoutAddress | undefined {
  if (!value || typeof value !== 'object') return undefined;

  const source = value as Record<string, unknown>;
  const address: GuestCheckoutAddress = {};

  for (const field of addressFields) {
    const fieldValue = source[field];
    if (fieldValue === undefined || fieldValue === null) continue;

    if (field === 'id') {
      const id = normalizeId(fieldValue);
      if (id) address.id = id;
      continue;
    }

    if (typeof fieldValue === 'string') {
      const normalized = normalizeText(fieldValue);
      if (normalized) address[field] = normalized as never;
      continue;
    }

    if (typeof fieldValue === 'number' && Number.isFinite(fieldValue)) {
      address[field] = fieldValue as never;
    }
  }

  if (createId && !address.id) address.id = createGuestShippingAddressId();
  return Object.keys(address).length > 0 ? address : undefined;
}

function normalizeCache(value: unknown): GuestCheckoutCache | null {
  if (!value || typeof value !== 'object') return null;

  const source = value as Record<string, unknown>;
  const cache: GuestCheckoutCache = {};
  const contactEmail = normalizeText(source.contactEmail);
  const shipping = normalizeAddress(source.shipping);
  const shippingAddressId = normalizeId(source.shippingAddressId) ?? shipping?.id;

  if (contactEmail) cache.contactEmail = contactEmail;
  if (shipping) cache.shipping = shipping;
  if (shippingAddressId) cache.shippingAddressId = shippingAddressId;

  return Object.keys(cache).length > 0 ? cache : null;
}

function resolveVisitorId(
  storage: GuestCheckoutStorage | null,
  visitorId?: string,
): string | undefined {
  const explicitVisitorId = normalizeText(visitorId);
  if (explicitVisitorId) return explicitVisitorId;
  if (!storage) return undefined;

  try {
    return normalizeText(storage.getItem(VISITOR_ID_STORAGE_KEY));
  } catch {
    return undefined;
  }
}

function getCacheKey(storage: GuestCheckoutStorage | null, visitorId?: string): string {
  const resolvedVisitorId = resolveVisitorId(storage, visitorId);
  return resolvedVisitorId
    ? `${GUEST_CHECKOUT_CACHE_BASE_KEY}-${resolvedVisitorId}`
    : GUEST_CHECKOUT_CACHE_BASE_KEY;
}

function readRawCache(key: string, storage: GuestCheckoutStorage | null): string | null {
  if (clearedCacheKeys.has(key)) return null;
  if (!storage) return memoryCaches.get(key) ?? null;

  try {
    const storedValue = storage.getItem(key);
    return storedValue ?? (memoryFallbackKeys.has(key) ? (memoryCaches.get(key) ?? null) : null);
  } catch {
    return memoryCaches.get(key) ?? null;
  }
}

function readCacheByKey(
  key: string,
  storage: GuestCheckoutStorage | null,
): GuestCheckoutCache | null {
  const raw = readRawCache(key, storage);
  if (!raw) return null;

  try {
    return normalizeCache(JSON.parse(raw));
  } catch {
    return null;
  }
}

function mergeCache(
  current: GuestCheckoutCache | null,
  patch: GuestCheckoutCachePatch,
): GuestCheckoutCache | null {
  const next: GuestCheckoutCache = { ...current };

  if ('contactEmail' in patch) {
    const contactEmail = normalizeText(patch.contactEmail);
    if (contactEmail) next.contactEmail = contactEmail;
    else delete next.contactEmail;
  }

  if ('shipping' in patch) {
    const shipping = normalizeAddress(patch.shipping, { createId: true });
    if (shipping) next.shipping = shipping;
    else {
      delete next.shipping;
      delete next.shippingAddressId;
    }
  }

  if ('shippingAddressId' in patch) {
    const shippingAddressId = normalizeId(patch.shippingAddressId);
    if (shippingAddressId) next.shippingAddressId = shippingAddressId;
    else delete next.shippingAddressId;
  } else if ('shipping' in patch && next.shipping?.id) {
    next.shippingAddressId = next.shipping.id;
  }

  return normalizeCache(next);
}

export function createGuestShippingAddressId(): string {
  try {
    if (typeof globalThis.crypto?.randomUUID === 'function') {
      return `guest-${globalThis.crypto.randomUUID()}`;
    }
  } catch {
    // Fall through to the local, non-persistent fallback id.
  }

  return `guest-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function readGuestCheckoutCache(
  options: GuestCheckoutCacheOptions = {},
): GuestCheckoutCache | null {
  const storage = resolveStorage(options.storage);
  const key = getCacheKey(storage, options.visitorId);
  const keys =
    key === GUEST_CHECKOUT_CACHE_BASE_KEY
      ? [GUEST_CHECKOUT_CACHE_BASE_KEY]
      : [key, GUEST_CHECKOUT_CACHE_BASE_KEY];

  for (const cacheKey of keys) {
    const cache = readCacheByKey(cacheKey, storage);
    if (cache) return cache;
  }

  return null;
}

export function saveGuestCheckoutCache(
  patch: GuestCheckoutCachePatch,
  options: GuestCheckoutCacheOptions = {},
): GuestCheckoutCache | null {
  const storage = resolveStorage(options.storage);
  const visitorId = resolveVisitorId(storage, options.visitorId);
  const key = getCacheKey(storage, visitorId);
  clearedCacheKeys.delete(key);
  const current = readGuestCheckoutCache({ storage, visitorId });
  const cache = mergeCache(current, patch);

  if (!cache) {
    clearGuestCheckoutCache({ storage, visitorId });
    return null;
  }

  const serialized = JSON.stringify(cache);
  memoryCaches.set(key, serialized);

  if (!storage) {
    memoryFallbackKeys.add(key);
    return cache;
  }

  try {
    storage.setItem(key, serialized);
    memoryFallbackKeys.delete(key);
  } catch {
    memoryFallbackKeys.add(key);
    // The in-memory copy remains available for the current page lifecycle.
  }

  return cache;
}

export function clearGuestCheckoutCache(options: GuestCheckoutCacheOptions = {}): void {
  const storage = resolveStorage(options.storage);
  const visitorId = resolveVisitorId(storage, options.visitorId);
  const key = getCacheKey(storage, visitorId);
  const keys =
    key === GUEST_CHECKOUT_CACHE_BASE_KEY
      ? [GUEST_CHECKOUT_CACHE_BASE_KEY]
      : [key, GUEST_CHECKOUT_CACHE_BASE_KEY];

  for (const cacheKey of keys) {
    memoryCaches.delete(cacheKey);
    memoryFallbackKeys.delete(cacheKey);
    if (!storage) continue;

    clearedCacheKeys.add(cacheKey);

    try {
      storage.removeItem(cacheKey);
    } catch {
      // Clearing is best effort when browser storage is unavailable.
    }
  }
}
